import { useEffect, useState } from "react";
import { api } from "../api";
import type { PromptSummary } from "../types";
import { fmtTs } from "../utils";

export function PromptsPanel({
  customerId,
  items,
  onChanged,
  flash,
}: {
  customerId: string;
  items: PromptSummary[];
  onChanged: () => Promise<void>;
  flash: (kind: "info" | "error", msg: string) => void;
}) {
  const [activeName, setActiveName] = useState<string | null>(items[0]?.name ?? null);
  const [activeContent, setActiveContent] = useState<string>("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [isNew, setIsNew] = useState(items.length === 0);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!activeName) {
      setActiveContent("");
      return;
    }
    api
      .readPrompt(customerId, activeName)
      .then((res) => {
        setActiveContent(res.content);
        setDirty(false);
      })
      .catch((e) => flash("error", e.message));
  }, [customerId, activeName, flash]);

  useEffect(() => {
    if (items.length === 0) {
      setIsNew(true);
      setActiveName(null);
    } else if (activeName && !items.find((i) => i.name === activeName)) {
      setActiveName(items[0].name);
    }
  }, [items, activeName]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draftContent.trim()) {
      flash("error", "Prompt 正文不能为空");
      return;
    }
    try {
      const info = await api.createPrompt(customerId, {
        title: draftTitle,
        name: draftName.trim() || undefined,
        content: draftContent,
      });
      setIsNew(false);
      setActiveName(info.name);
      setDraftTitle("");
      setDraftName("");
      setDraftContent("");
      await onChanged();
      flash("info", `已新建 ${info.name}`);
    } catch (err: unknown) {
      flash("error", (err as Error).message);
    }
  };

  const save = async () => {
    if (!activeName) return;
    try {
      await api.updatePrompt(customerId, activeName, activeContent);
      await onChanged();
      flash("info", "已保存");
      setDirty(false);
    } catch (err: unknown) {
      flash("error", (err as Error).message);
    }
  };

  const remove = async (name: string) => {
    if (!confirm(`真的要删除 ${name}？`)) return;
    try {
      await api.deletePrompt(customerId, name);
      if (activeName === name) setActiveName(null);
      await onChanged();
    } catch (err: unknown) {
      flash("error", (err as Error).message);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
      <aside className="card flex flex-col">
        <div className="flex items-center justify-between border-b border-ink-100 px-3 py-2">
          <h3 className="text-sm font-semibold">所有版本</h3>
          <button
            className="btn-secondary text-xs"
            onClick={() => {
              setIsNew(true);
              setActiveName(null);
            }}
          >
            + 新版本
          </button>
        </div>
        {items.length === 0 ? (
          <p className="px-3 py-4 text-xs text-ink-500">还没有 Prompt 模板</p>
        ) : (
          <ul className="divide-y divide-ink-100 overflow-y-auto scrollbar-thin">
            {items.map((p) => (
              <li key={p.name}>
                <button
                  className={
                    activeName === p.name && !isNew
                      ? "block w-full px-3 py-2 text-left bg-indigo-50"
                      : "block w-full px-3 py-2 text-left hover:bg-ink-50"
                  }
                  onClick={() => {
                    setActiveName(p.name);
                    setIsNew(false);
                  }}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-xs">{p.name}</span>
                    <span className="text-[10px] text-ink-400">{fmtTs(p.modified_at)}</span>
                  </div>
                  <div className="mt-0.5 truncate text-xs text-ink-500">{p.preview}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="card flex flex-col">
        {isNew ? (
          <form onSubmit={create} className="flex h-full flex-col">
            <div className="grid grid-cols-2 gap-3 border-b border-ink-100 px-5 py-3">
              <div>
                <label className="label">标题（用于自动生成文件名）</label>
                <input
                  className="input"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  placeholder="例如：宝妈日常 v2"
                />
              </div>
              <div>
                <label className="label">指定文件名（可选，自动加 .md）</label>
                <input
                  className="input font-mono text-xs"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="不填则按时间戳 + 标题派生"
                />
              </div>
            </div>
            <textarea
              className="textarea m-5 flex-1 min-h-[24rem]"
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
              placeholder={"# 账号定位\n...\n\n# 内容方向\n...\n\n# 模板\n..."}
              required
            />
            <div className="flex justify-end gap-2 border-t border-ink-100 px-5 py-3">
              {items.length > 0 && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setIsNew(false);
                    setActiveName(items[0]?.name ?? null);
                  }}
                >
                  取消
                </button>
              )}
              <button type="submit" className="btn-primary">
                保存为新版本
              </button>
            </div>
          </form>
        ) : activeName ? (
          <>
            <div className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
              <div>
                <div className="font-mono text-sm">{activeName}</div>
                <div className="text-xs text-ink-500">
                  {dirty ? <span className="text-amber-700">未保存改动</span> : "已同步"}
                </div>
              </div>
              <div className="flex gap-2">
                <button className="btn-danger" onClick={() => remove(activeName)}>
                  删除
                </button>
                <button className="btn-primary" disabled={!dirty} onClick={save}>
                  保存
                </button>
              </div>
            </div>
            <textarea
              className="textarea m-5 flex-1 min-h-[24rem]"
              value={activeContent}
              onChange={(e) => {
                setActiveContent(e.target.value);
                setDirty(true);
              }}
            />
          </>
        ) : (
          <div className="flex h-full items-center justify-center px-5 py-10 text-sm text-ink-500">
            从左侧选一份模板，或点「+ 新版本」开始
          </div>
        )}
      </section>
    </div>
  );
}
